import * as React from 'react';
import Popup from 'reactjs-popup';
import { observer } from 'mobx-react';
import { Icon } from 'components/widgets/icons';
import { TrackersData, ITracker, TRACKER_SRC, displayedDailyGoal, conversionForTracker } from 'data/TrackersData';
import { observable, computed } from 'mobx';
import { openPopop, closePopup, openConfirmPopup } from '../widgets/Popup';

import './ManageTrackersSubPage.pcss';
import { ToggleButton } from 'components/widgets/ToggleButton';
import { TextInput, FloatInput } from 'components/widgets/TextInput';
import { FitbitBanner } from './ManageTrackers/FitbitBanner';
import { IconButton } from 'components/widgets/IconButton';
import { Button } from 'components/widgets/Button';
import { CreateTrackerPopup } from './ManageTrackers/CreateTrackerPopup';
import SortableList, { SortableListItem } from 'components/widgets/SortableList';
import { SelectTrackerIconPopup } from './ManageTrackers/SelectTrackerIconPopup';
import { IEIncompatible } from 'components/widgets/IEIncompatible';
import {UIState} from "data/UIState";
import {Alert} from "../../data/Alert";

@observer
export class ManageTrackersSubPage extends React.Component {

  render() {

    return (
      <div className="manageTrackers">
        
        <div className="top">
          <FitbitBanner />
        </div> 
        
        <IEIncompatible message="Drag and drop re-ordering will not work in your browser." />

        <SortableList onReorderRequest={TrackersData.handleReorderRequest} headerItem={<MTTableHeader />} footerItem={<MTTableFooter />} className="table">
          {TrackersData.trackers.map((tracker, idx)=>{
            return (
              <SortableListItem index={idx} id={tracker._id} key={tracker._id} disabled={false}>
                <TrackerItem key={tracker._id} tracker={tracker} idx={idx} />
              </SortableListItem>                    
            );
          })}
        </SortableList>
      </div>
    );
  }
}

const MTTableFooter = () =>
  <div className="foot">
    <Button flat onClick={()=>{ openPopop(<CreateTrackerPopup />); }}>CREATE TRACKER</Button>
  </div>  
;

const MTTableHeader = () => 
  <div className="head tchild">
    <div className="drag"></div>
    <div className="track">Tracking</div>
    <div className="icon">Icon</div>
    <div className="name">Name</div>
    <div className="goal">Goal/Limit</div>
    <div className="unit">Unit</div>
    <div className="src">Source</div>
    <div className="opts">Options</div>
  </div>
;


interface TrackerItem_P {
  tracker:ITracker;
  idx:number;
}

@observer
class TrackerItem extends React.Component<TrackerItem_P> {
  @observable disabled:boolean = false; 
    
  handleToggleTracker = () => {
    this.disabled = true;
    TrackersData.toggleTracking(this.props.tracker)
    .then()
    .catch(()=>{
      Alert.alert(`Failed to ${this.props.tracker.activeTracking?'disable':'enable'} tracker ${this.props.tracker.name}. Please try again.`);
    })
    .then(()=>{
      this.disabled = false;
    });
  }

  @computed
  get dailyGoal() {
    return displayedDailyGoal(this.props.tracker);
  }

  @observable editMode:boolean = false;
  
  handleSetEditMode = (editMode:boolean) => {
    this.editMode = editMode;
  }


  render() {
    const { tracker, idx } = this.props;
    
    return (
      <div className={`item ${0==idx%2?'odd':'even'}${this.editMode?' edit':''} tchild`}>
          <div className="drag"><i className="material-icons noselect">drag_handle</i></div>
          <div className="track"><ToggleButton on={tracker.activeTracking} disabled={this.disabled} onClick={this.handleToggleTracker} /></div>
          { tracker.src===TRACKER_SRC.FITBIT ? 
            <React.Fragment>
              <div className="icon"><div><Icon iconName={tracker.iconName} color="#666666" /></div></div>
              <div className="name"><span className="txt">{tracker.name}</span></div>
              <div className="goal"><span className="txt">{this.dailyGoal}</span></div>
              <div className="unit"><span className="txt">{tracker.unit}</span></div>
              <div className="src"><span className="txt">Fitbit</span></div>
              <div className="opts"><TrackerMOM tracker={this.props.tracker} /></div>        
            </React.Fragment>
            :
            <React.Fragment>
              <EditableTrackerItemFields tracker={tracker} onSetEditMode={this.handleSetEditMode} editMode={this.editMode} disabled={this.disabled} />
            </React.Fragment>
          }
      </div>
    )
  }
}

interface EditableTrackerItemFields_P {
  tracker:ITracker;
  editMode:boolean;
  onSetEditMode:(editMode:boolean)=>void;
  disabled:boolean;
}

@observer
class EditableTrackerItemFields extends React.Component<EditableTrackerItemFields_P> {

  //@observable tracker:ITracker;
  @observable saving:boolean = false;
  @observable name:string;
  @observable iconName:string;
  @observable dailyGoal:number;
  @observable unit:string;

  constructor(props:EditableTrackerItemFields_P) {
    super(props);
    this.init(props.tracker);
  }

  init = (tracker:ITracker) => {
    this.name = tracker.name?tracker.name:'';
    this.iconName = tracker.iconName?tracker.iconName:'';
    let conversion  = conversionForTracker(tracker.id);
    this.dailyGoal = (''+tracker.dailyGoal===''?tracker.dailyGoal : conversion ? conversion(tracker.dailyGoal) : tracker.dailyGoal);
    this.unit = tracker.unit?tracker.unit:''; 
    this.props.onSetEditMode(false);
  }

  handleClickIcon = () => {    
    openPopop(<SelectTrackerIconPopup tracker={this.props.tracker} />);
  }

  @computed 
  get isADefaultTracker() {
    return this.props.tracker.isADefaultTracker;
  }

  @computed
  get isDirty() { 
    return this.name !== (this.props.tracker.name?this.props.tracker.name:'') ||
    this.iconName !== (this.props.tracker.iconName?this.props.tracker.iconName:'') ||
    this.dailyGoal !== (!isNaN(this.props.tracker.dailyGoal)?this.props.tracker.dailyGoal:'') ||
    this.unit !== (this.props.tracker.unit?this.props.tracker.unit:'');
  }

  handleClickDone = () => {
    if(this.dailyGoal+''==='') {
      Alert.alert('Goal is not an optional field!');
    } else {
      if(this.isDirty) {
        this.saving = true;
        let conversion  = conversionForTracker(this.props.tracker.id);
        let trackerData:ITracker = { ...this.props.tracker, name:this.name, iconName:this.iconName, unit:this.unit, dailyGoal:conversion?conversion(this.dailyGoal, true):this.dailyGoal };
        TrackersData.updateTracker(trackerData)        
        .then(this.init)
        .catch(()=>{
          Alert.alert('Failed to save settings for '+trackerData.name+'. Please make sure your Internet connection is stable and try again.');
        })
        .then(()=>{
          this.saving = false;
        });      

      } else {
        this.props.onSetEditMode(false);
      }
    }
  }

  handleEnterEditMode = () => {
    this.props.onSetEditMode(true);
  }

  @computed
  get disabled() {
    return this.props.disabled || this.saving;
  }

  handleClickMoreVert = () => {

  }

  render() {
    const { tracker, editMode } = this.props;
    return (
      <React.Fragment>
        <div className="icon">
          {tracker.isADefaultTracker ?
            <div>
              <Icon iconName={tracker.iconName} color="#666666" />
            </div>
            :
            <a onClick={this.handleClickIcon}>
              <Icon iconName={tracker.iconName} color="#666666" />
            </a>
          }
        </div>
        <div className="name">
          {this.isADefaultTracker ? <span className="txt">{this.props.tracker.name}</span> :
            <TextInput 
              onFocus={this.handleEnterEditMode} 
              value={this.name} 
              onChangeValue={(v)=>{ this.name=v; }}
              disabled={this.props.disabled}
            />
          }

        </div>
        <div className="goal">
          <FloatInput 
            onFocus={this.handleEnterEditMode} 
            value={this.dailyGoal} 
            onChangeValue={(v)=>{ this.dailyGoal= v; }}
            disabled={this.props.disabled}
          />        
        </div>
        <div className="unit">
          {this.isADefaultTracker ? <span className="txt">{this.props.tracker.unit}</span> :
            <TextInput 
              onFocus={this.handleEnterEditMode} 
              value={this.unit} 
              onChangeValue={(v)=>{ this.unit=v; }}
              disabled={this.props.disabled}
            />        
          }
        </div>
        <div className="src"><span className="txt">Manual (Strive)</span></div>
        <div className="opts">
          {editMode ? 
            <Button onClick={this.handleClickDone}>done</Button>
          :
            <TrackerMOM tracker={this.props.tracker} />
          }
        
        </div>        
      </React.Fragment>
    );   
  }
}



@observer
class TrackerMOM extends React.Component<{tracker:ITracker}> {

  handleConfirmDeleteManualData = () => {
    TrackersData.deleteDailyValues(this.props.tracker)
    .then(()=>{
      closePopup();
    })
    .catch(()=>{
      // closePopup();
      Alert.alert(`Failed to delete manual data. Please make sure your Internet connection is stable and try again.`);
    });
  }

  handleConfirmDeleteCustomTracker = () => {
    TrackersData.deleteCustomTracker(this.props.tracker)
    .then(()=>{
      closePopup();
    })
    .catch(()=>{
      // closePopup();
      Alert.alert(`Failed to delete “${this.props.tracker.name}”. Please make sure your Internet connection is stable and try again.`);
    });
  }

  handleClickDeleteManualData = () => {    
    openConfirmPopup(`This will remove all stored data associated with “${this.props.tracker.name}”`, this.handleConfirmDeleteManualData);
  }

  handleClickDeleteTracker = () => {
    openConfirmPopup(`This will permanently delete “${this.props.tracker.name}” from Strive`, this.handleConfirmDeleteCustomTracker);    
  }

  @computed
  get hasManualData() {
    return this.props.tracker.dailyValues && Object.keys(this.props.tracker.dailyValues).length > 0;
  }

  render() {
    const { tracker } = this.props;

    return (

      <Popup trigger={<div className="MOMLauncher"><IconButton>more_vert</IconButton></div>} position={["left top", "left bottom"]} arrow={false} className={"tracker-mom"} >
        <div className="MOM">
          {tracker.isADefaultTracker ?
            (this.hasManualData?
                <React.Fragment>
                  {tracker.src==TRACKER_SRC.FITBIT ?
                    <React.Fragment>
                      <MOMOption icon="remove_circle_outline" label={`Delete manual data for “${tracker.name}”`}  onClick={this.handleClickDeleteManualData} />
                      <hr/>
                      <p>“{tracker.name}” has manual data as well as data from Fitbit. There is no harm in keeping this data, but you may delete it if you wish</p>
                    </React.Fragment>
                    :
                    <MOMOption icon="remove_circle_outline" label={`Delete data for “${tracker.name}”`}  onClick={this.handleClickDeleteManualData} />
                  }
                </React.Fragment>
                :
                <React.Fragment>
                  <p>No actions for “{tracker.name}” </p>
                </React.Fragment>
            )
            :
            <React.Fragment>
              <MOMOption icon="delete_forever" label={`Remove “${tracker.name}”`}  onClick={this.handleClickDeleteTracker} />
              <hr/>
              <p>You created this metric. Removing will delete “{tracker.name}” from Strive.</p>
            </React.Fragment>
          }
        </div>
      </Popup>
    );
  }
}




const MOMOption = (props:{label:string, icon:string, onClick:()=>void}) => 
  <div className="option" onClick={props.onClick}>
    <i className="material-icons">{props.icon}</i>
    {props.label}
  </div>
;
